import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  RefreshCw,
  MapPin,
  Utensils,
  Clock,
  ArrowRight,
  Navigation,
  Bookmark,
  BookmarkCheck,
  Download,
  Share2,
  Home,
  Trash2,
  ChevronUp,
  ChevronDown,
  Plus,
  Search,
  X,
  Pencil,
} from "lucide-react";
import { z } from "zod";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { ProPaywall } from "@/components/ProPaywall";
import { MobilityTicketCard } from "@/components/MobilityTicketCard";
import { NavigationReadinessBar } from "@/components/NavigationReadinessBar";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import { exportProPlanPdf } from "@/lib/pro/pdf-export";
import { classifyGeoScope, findCountryInText, needsRegionalPlanner } from "@/lib/geo-scope";
import { pickRegionalBases, type RegionalBase } from "@/lib/catalog/regions";
import { readPlanHandoff, clearPlanHandoff, summarizePlanHandoff } from "@/lib/plan-handoff";
import {
  extractTripRequirements,
  requiredGlobalTransport,
  checkPlanCompliance,
  type ComplianceViolation,
  isPlanAcceptable,
} from "@/lib/trip-requirements";
import {
  buildRequestContract,
  contractQuestions,
  type ContractQuestion,
} from "@/lib/request-contract";
import {
  CURATED_ROUTE_TEMPLATES,
  matchTemplateByName,
  suggestTemplates,
  type CuratedRouteTemplate,
} from "@/lib/catalog/curated-route-templates";
import {
  experienceModeCopy,
  recommendedRouteDays,
  shouldAutoSelectFullRoute,
} from "@/lib/experience-types";

import {
  compareAccommodationZones,
  ACCOMMODATION_DISCLAIMER,
  type AccommodationZone,
} from "@/lib/accommodation-zones";
import { buildRoutePreviewUrl } from "@/lib/navigation-url";
import { corridorForTemplate } from "@/lib/catalog/corridor-routes";
import {
  corridorRequiresAvoidHighways,
  strictCorridorNavigationStops,
} from "@/lib/pro/strict-corridor";
import { buildRouteSkeleton } from "@/lib/pro/route-skeleton";
import { isValidMultiDestinationPlan, parseMultiDestinationPlan, type MultiDestinationPlan } from "@/lib/pro/multi-destination";
import { isFunctionalRequestWord, parseProRequestContract } from "@/lib/pro/required-stops";
import { preflightProGeneration } from "@/lib/pro/generation-preflight";
import { resolveMealPreference } from "@/lib/meal-preference";

import { isMaterialChange, type GenerationContextInput } from "@/lib/pro/generation-context";
import {
  detectMotorcycleMode,
  motorcycleDirective,
  transportModeLabel,
  type TransportMode,
} from "@/lib/transport-modes";
import { groupModeSegments } from "@/lib/pro/multimodal";
import { buildModeSegmentUrls } from "@/lib/navigation-url";
import { extractLocalSpecialties, specialtyPromptDirective } from "@/lib/local-specialties";
import { buildProShareUrl } from "@/lib/share-url";
import { getPlaceContactInfo, getPlacePhoto, type PlaceContactInfo } from "@/lib/maps.functions";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GoogleRouteButton } from "@/components/GoogleRouteButton";
import { demoFn } from "@/lib/demo-mode";
import { demoProPlan } from "@/lib/demo-fixtures";

import {
  checkProAccess,
  generateProItinerary,
  listMyProItineraries,
  regenerateProDay,
  getProItinerary,
  saveProItinerary,
  updateProPlan,
  searchProPlace,
} from "@/lib/pro/pipeline.functions";

const searchSchema = z.object({ id: z.string().uuid().optional() });

export const Route = createFileRoute("/pro")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Routify Pro — planeador inteligente" },
      {
        name: "description",
        content:
          "Planeador multi-dia e multi-cidade da Routify, com otimização de rota e adaptação ao meio de transporte.",
      },
    ],
  }),
  component: ProPage,
});

const INTERESTS = [
  "cultural",
  "food",
  "nature",
  "viewpoint",
  "shopping",
  "nightlife",
  "family",
  "hidden",
] as const;

const TRANSPORT_LABEL: Record<string, string> = {
  walk: "A pé",
  bike: "Bicicleta",
  car: "Carro",
  motorcycle: "Mota",
  transit: "Transportes públicos",
};
const PACE_LABEL: Record<string, string> = {
  slow: "Calmo",
  normal: "Normal",
  intense: "Intenso",
};
const MOOD_LABEL: Record<string, string> = {
  romantic: "Romântico",
  family: "Familiar",
  adventure: "Aventura",
  relaxed: "Relaxado",
  cultural: "Cultural",
  foodie: "Gastronómico",
};
const INTEREST_LABEL: Record<string, string> = {
  cultural: "Cultural",
  food: "Gastronomia",
  nature: "Natureza",
  viewpoint: "Miradouros",
  shopping: "Compras",
  nightlife: "Vida noturna",
  family: "Família",
  hidden: "Lugares secretos",
};

type PlanShape = Awaited<ReturnType<typeof generateProItinerary>>;
type ListItem = { id: string; title: string; status: string; created_at: string };

function mapsUrlForStop(s: { name: string; lat?: number; lng?: number; placeId?: string }) {
  const q = s.placeId ? `${s.lat},${s.lng}` : `${s.name}`;
  const params = new URLSearchParams({ api: "1", query: q });
  if (s.placeId) params.set("query_place_id", s.placeId);
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

function navUrlForStop(s: { lat?: number; lng?: number; placeId?: string; name: string }) {
  const params = new URLSearchParams({
    api: "1",
    destination: s.lat && s.lng ? `${s.lat},${s.lng}` : s.name,
  });
  if (s.placeId) params.set("destination_place_id", s.placeId);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function ProPage() {
  const { lang } = useLang();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const checkFn = demoFn(useServerFn(checkProAccess), () => ({ hasPro: true }) as never);
  const generateFn = demoFn(
    useServerFn(generateProItinerary),
    (opts) =>
      demoProPlan({
        days: (opts as { data?: { days?: number } })?.data?.days ?? 2,
        city: (opts as { data?: { cities?: string[] } })?.data?.cities?.[0],
      }) as never,
  );
  const listFn = useServerFn(listMyProItineraries);
  const regenFn = demoFn(useServerFn(regenerateProDay), () => ({ days: [] }) as never);
  const getFn = useServerFn(getProItinerary);
  const saveFn = useServerFn(saveProItinerary);
  const updateFn = useServerFn(updateProPlan);
  const searchFn = demoFn(useServerFn(searchProPlace), () => ({ results: [] }) as never);
  const contactFn = demoFn(useServerFn(getPlaceContactInfo), () => null as never);
  const photoFn = demoFn(useServerFn(getPlacePhoto), () => ({ photoUrl: null }) as never);

  const [hasPro, setHasPro] = useState<boolean | null>(null);
  const [prompt, setPrompt] = useState("");
  const [cities, setCities] = useState("");
  const [accommodation, setAccommodation] = useState("");
  const [originCoords, setOriginCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [days, setDays] = useState(3);
  /** Origem do valor de Dias: "text" (duração escrita) ou "manual" (edição do utilizador). */
  const [daysSource, setDaysSource] = useState<"text" | "manual" | null>(null);
  /** Locais nomeados exigidos no pedido ("que inclua X, Y, Z"). Nunca são cidades. */
  const [requiredStops, setRequiredStops] = useState<string[]>([]);
  /** Cidades editadas à mão deixam de ser sobrescritas pelo contrato do texto. */
  const citiesTouchedRef = useRef(false);
  /** Contrato explícito da tentativa; enquanto existir, vence todo o estado do formulário. */
  const [multiDestinationPlan, setMultiDestinationPlan] = useState<MultiDestinationPlan | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transport, setTransport] = useState<"walk" | "bike" | "car" | "transit">("walk");
  /** Modos locais adicionais (multimodal). Vazio = comportamento de sempre. */
  const [localModes, setLocalModes] = useState<TransportMode[]>([]);
  const [wantsRental, setWantsRental] = useState(false);
  /** Modo mota: circula na rede rodoviária, mas com regras próprias. */
  const [motorcycle, setMotorcycle] = useState(false);
  /** Assinatura do último pedido gerado — deteta pedidos materialmente novos. */
  const lastContextRef = useRef<GenerationContextInput | null>(null);
  const [pace, setPace] = useState<"slow" | "normal" | "intense">("normal");
  const [interests, setInterests] = useState<string[]>(["cultural", "viewpoint"]);
  const [generating, setGenerating] = useState(false);
  /** Trava pedidos concorrentes (duplo clique nas opções de reparação). */
  const generatingRef = useRef(false);
  /** Último roteiro conhecido — mantém a reparação válida mesmo sem estado. */
  const lastPlanIdRef = useRef<string | null>(null);
  const [plan, setPlan] = useState<PlanShape | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [shareSlug, setShareSlug] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [regeneratingDay, setRegeneratingDay] = useState<number | null>(null);
  const [history, setHistory] = useState<ListItem[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [addSearchFor, setAddSearchFor] = useState<{ day: number; meal: boolean } | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<
    Array<{
      id: string;
      name: string;
      lat: number;
      lng: number;
      address: string;
      rating: number | null;
    }>
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pendingReplace, setPendingReplace] = useState<{ day: number; index: number } | null>(null);
  // Contrato do pedido: clarificação de experiência icónica, zonas de
  // alojamento e verificação de cumprimento do plano gerado.
  const [templateOptions, setTemplateOptions] = useState<CuratedRouteTemplate[] | null>(null);
  const [chosenTemplate, setChosenTemplate] = useState<CuratedRouteTemplate | null>(null);
  const [templateMode, setTemplateMode] = useState<"full" | "partial" | null>(null);
  const [routeReturn, setRouteReturn] = useState<"excluded" | "included" | null>(null);
  const [otherTemplate, setOtherTemplate] = useState("");
  const [zones, setZones] = useState<AccommodationZone[]>([]);
  const [violations, setViolations] = useState<ComplianceViolation[]>([]);
  // Perguntas curtas e gratuitas do contrato (antes de IA, Maps ou créditos).
  const [contractQuestionsList, setContractQuestionsList] = useState<ContractQuestion[]>([]);
  const [detailStop, setDetailStop] = useState<PlanShape["days"][number]["stops"][number] | null>(null);
  const [detailInfo, setDetailInfo] = useState<PlaceContactInfo | null>(null);
  const [detailPhoto, setDetailPhoto] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function openStopDetails(stop: PlanShape["days"][number]["stops"][number]) {
    setDetailStop(stop);
    setDetailPhoto(stop.photoUrl ?? null);
    setDetailInfo(null);
    if (!stop.placeId) return;
    setDetailLoading(true);
    try {
      const [info, photo] = await Promise.all([
        contactFn({ data: { placeId: stop.placeId } }),
        stop.photoUrl ? Promise.resolve({ photoUrl: stop.photoUrl }) : photoFn({ data: { placeId: stop.placeId } }),
      ]);
      setDetailInfo(info);
      setDetailPhoto(photo.photoUrl ?? null);
    } catch {
      toast.error("Não foi possível carregar os detalhes deste local.");
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Handoff vindo do Premium: hidrata o formulário com o contrato completo
  // (dias, cidades reais, transporte interurbano, interesses, alojamento) e só
  // depois limpa o armazenamento, para nada se perder a meio.
  useEffect(() => {
    const handoff = readPlanHandoff();
    if (!handoff) return;
    // Um handoff é um pedido novo. Limpar integralmente o resultado anterior
    // antes de hidratar o contrato evita herdar 5 dias/cidades/reparações.
    setPlan(null);
    setViolations([]);
    setTemplateOptions(null);
    setChosenTemplate(null);
    setTemplateMode(null);
    setRouteReturn(null);
    lastContextRef.current = null;
    lastPlanIdRef.current = null;
    setPrompt(handoff.prompt);
    const handoffPlan = isValidMultiDestinationPlan(handoff.multiDestinationPlan)
      ? handoff.multiDestinationPlan
      : parseMultiDestinationPlan(handoff.prompt);
    setMultiDestinationPlan(handoffPlan);
    setCities(
      handoffPlan
        ? handoffPlan.stops.map((stop) => stop.destination).join(", ")
        : handoff.cities?.length
          ? handoff.cities.join(", ")
          : handoff.scopeName,
    );
    if (handoffPlan) setDays(handoffPlan.stops.reduce((sum, stop) => sum + stop.days, 0));
    else if (handoff.days && handoff.days > 0) setDays(handoff.days);
    if (handoff.transport && ["walk", "bike", "car", "transit"].includes(handoff.transport)) {
      setTransport(handoff.transport as "walk" | "bike" | "car" | "transit");
    }
    if (handoff.interests?.length) setInterests(handoff.interests);
    if (handoff.startDate) setStartDate(handoff.startDate);
    if (handoff.endDate) setEndDate(handoff.endDate);
    clearPlanHandoff();
    const summary = summarizePlanHandoff(handoff, "pt");
    toast.info(
      `Pedido trazido do Premium: ${summary.scopeLabel}` +
        (summary.days ? ` · ${summary.days} dias` : "") +
        ` · ${summary.transportLabel}` +
        (summary.cities.length ? ` · ${summary.cities.join(", ")}` : ""),
    );
  }, []);

  // Contrato de entrada derivado do texto, pela ordem obrigatória:
  // duração explícita → locais obrigatórios → destinos. O campo "Dias" só é
  // preenchido a partir do texto enquanto o utilizador não o editar à mão.
  useEffect(() => {
    const parsed = parseMultiDestinationPlan(prompt);
    const contract = parseProRequestContract(prompt);
    setRequiredStops((current) =>
      current.join("|") === contract.requiredStops.join("|") ? current : contract.requiredStops,
    );
    setMultiDestinationPlan((current) =>
      JSON.stringify(current ?? null) === JSON.stringify(parsed ?? null) ? current : parsed,
    );
    if (parsed) {
      const total = parsed.stops.reduce((sum, stop) => sum + stop.days, 0);
      setDays((current) => (current === total ? current : total));
      setDaysSource("text");
      if (!citiesTouchedRef.current) setCities(parsed.stops.map((s) => s.destination).join(", "));
      return;
    }
    if (contract.totalDays && daysSource !== "manual") {
      setDays((current) => (current === contract.totalDays ? current : contract.totalDays!));
      setDaysSource("text");
    }
    if (!citiesTouchedRef.current && contract.destinationBases.length > 0) {
      const next = contract.destinationBases.join(", ");
      setCities((current) => (current === next ? current : next));
    }
  }, [prompt, daysSource]);



  useEffect(() => {
    if (!isAuthenticated) return;
    checkFn()
      .then((r) => setHasPro(r.hasPro))
      .catch(() => setHasPro(false));
    listFn()
      .then((r) => setHistory(r as ListItem[]))
      .catch(() => {});
  }, [isAuthenticated, checkFn, listFn]);

  // Load a specific plan from ?id=
  useEffect(() => {
    if (!isAuthenticated || !search.id) return;
    getFn({ data: { id: search.id } })
      .then((row) => {
        const p = row.plan as PlanShape;
        setPlan({ ...p, id: row.id });
        lastPlanIdRef.current = row.id;
        setViolations((p.compliance?.violations ?? []) as ComplianceViolation[]);
        setIsSaved(Boolean((row as { saved?: boolean }).saved));
        setShareSlug((row as { share_slug?: string | null }).share_slug ?? null);
      })
      .catch(() => toast.error("Não consegui abrir esse roteiro."));
  }, [isAuthenticated, search.id, getFn]);

  function toggleInterest(id: string) {
    setInterests((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  /** Patch de uma opção de reparação escolhida pelo utilizador. */
  type RepairOverride = {
    repairStrategy: string;
    cities: string[];
    days: number;
    transport: string;
    templateId: string;
    templateMode: "full" | "partial";
    accommodationZoneMin: number;
    themes: string[];
    routeReturn?: "excluded" | "included";
  };

  /**
   * Resolve experiências curadas compatíveis com o pedido, sem exigir que o
   * utilizador reescreva o texto. Vai relaxando os filtros (temas → dias →
   * país) para nunca devolver um seletor vazio quando existe catálogo.
   */
  function resolveExperienceOptions(): CuratedRouteTemplate[] {
    const scopeSources = [cities, prompt, plan?.days?.map((d) => d.city).join(", ") ?? ""];
    let countryCode: string | undefined;
    for (const source of scopeSources) {
      if (!source?.trim()) continue;
      const scope = classifyGeoScope(source, { language: "pt" });
      if (scope.countryCode) { countryCode = scope.countryCode; break; }
      const inText = findCountryInText(source, "pt");
      if (inText) { countryCode = inText.code; break; }
      const byAnchor = CURATED_ROUTE_TEMPLATES.find((t) =>
        t.anchors.some((a) => source.toLowerCase().includes(a.toLowerCase())),
      );
      if (byAnchor) { countryCode = byAnchor.countryCodes[0]; break; }
    }
    const attempts = [
      { countryCode, category: "scenic_train" as const, days, themes: interests },
      { countryCode, category: "scenic_train" as const, days },
      { countryCode, category: "scenic_train" as const },
      { countryCode },
    ];
    for (const attempt of attempts) {
      const options = suggestTemplates(attempt);
      if (options.length) return options;
    }
    return [];
  }

  /** Escolha directa no seletor ⇒ reconstrói o roteiro sem novo crédito. */
  function chooseExperience(template: CuratedRouteTemplate) {
    void applyExperienceMode(template, "full");
  }

  /**
   * Aplica o formato escolhido (percurso completo ou troço): atualiza cidades,
   * dias e transporte, regenera com `repairOfId` (idempotente, sem novo
   * crédito) e devolve sempre feedback visual.
   */
  async function applyExperienceMode(
    template: CuratedRouteTemplate,
    mode: "full" | "partial",
    returnChoice?: "excluded" | "included",
  ) {
    if (generatingRef.current) {
      toast.info("Já estou a reconstruir o roteiro — aguarda um instante.");
      return;
    }
    setChosenTemplate(template);
    setTemplateOptions(null);
    setTemplateMode(mode);
    if (mode === "full" && template.transport === "car" && !returnChoice) {
      setRouteReturn(null);
      return;
    }
    const selectedReturn = mode === "full" && template.transport === "car"
      ? (returnChoice ?? "excluded")
      : "excluded";
    setRouteReturn(selectedReturn);
    const routeDays = selectedReturn === "included" ? Math.max(1, days - 1) : days;
    const dayPatch = days;
    // O esqueleto da experiência define a geografia: cada dia avança para a
    // base seguinte, sem repetir cidades e terminando na âncora final.
    const skeleton = buildRouteSkeleton({
      template,
      days: routeDays,
      mode,
      startRegion: cities || accommodation || prompt,
    });
    const cityPatch = skeleton.cities;
    if (dayPatch !== days) setDays(dayPatch);
    setCities(cityPatch.join(", "));
    await onGenerate(null, {
      repairStrategy:
        mode === "full" ? "fewer_cities_full_experience" : "more_nature_partial_experience",
      cities: cityPatch,
      days: dayPatch,
      transport,
      templateId: template.id,
      templateMode: mode,
      accommodationZoneMin: 2,
      themes: interests,
      routeReturn: selectedReturn,
    });
  }


  /** Resposta a uma pergunta gratuita: enriquece o pedido e volta a gerar. */
  function answerContractQuestion(label: string) {
    setContractQuestionsList([]);
    setPrompt((current) => `${current.trim()} — ${label}.`);
    setTimeout(() => { void onGenerate(null); }, 0);
  }

  async function onGenerate(e: React.FormEvent | null, override?: RepairOverride) {
    e?.preventDefault();
    if (!prompt.trim()) {
      toast.error("Descreve a viagem que queres.");
      return;
    }

    // 0) Pedido materialmente novo ⇒ contexto limpo. Nada da geração anterior
    // (experiência curada, reparação, zonas, erros) pode sobreviver.
    const contextInput: GenerationContextInput = {
      prompt,
      cities,
      days,
      transport,
      accommodation,
      startDate,
      endDate,
    };
    const freshRequest = !override && isMaterialChange(lastContextRef.current, contextInput);
    let carriedTemplate = chosenTemplate;
    let carriedMode = templateMode;
    if (freshRequest) {
      carriedTemplate = null;
      carriedMode = null;
      setChosenTemplate(null);
      setTemplateOptions(null);
      setTemplateMode(null);
      setRouteReturn(null);
      setOtherTemplate("");
      setZones([]);
      setViolations([]);
      setPlan(null);
      setShareSlug(null);
      setIsSaved(false);
      lastPlanIdRef.current = null;
    }

    // 1) Ler o pedido como contrato verificável (sem custo, antes de tudo).
    const requestTemplate =
      (override?.templateId
        ? CURATED_ROUTE_TEMPLATES.find((candidate) => candidate.id === override.templateId)
        : null) ??
      carriedTemplate ??
      matchTemplateByName(prompt);
    const requirements = extractTripRequirements(prompt, {
      language: "pt",
      scopeText: cities || prompt,
      curatedExperienceChosen: Boolean(carriedTemplate) || Boolean(override?.templateId),
      curatedExperienceCategory: requestTemplate?.category,
    });
    // Contrato vinculativo do pedido: tudo o que foi pedido é verificável.
    const contract = buildRequestContract({
      text: prompt,
      language: "pt",
      scopeText: cities || prompt,
      formInterests: interests,
      mealChoice: "restaurant",
      eatMinutes: 90,
      formDays: days,
      formTransport: transport,
      curatedExperienceChosen: Boolean(carriedTemplate) || Boolean(override?.templateId),
      curatedExperienceCategory: requestTemplate?.category,
    });
    // Faltando informação indispensável, pergunta-se antes de gastar crédito.
    const pendingQuestions = override
      ? []
      : contractQuestions(contract).filter((q) => q.options.length > 0);
    if (pendingQuestions.length > 0) {
      setContractQuestionsList(pendingQuestions);
      return;
    }
    setContractQuestionsList([]);

    const wantsMotorcycle = motorcycle || detectMotorcycleMode(prompt);
    if (wantsMotorcycle && !motorcycle) setMotorcycle(true);
    const specialties = extractLocalSpecialties(prompt);
    // Contrato do pedido: duração → locais obrigatórios → destinos.
    const requestContract = parseProRequestContract(prompt, {
      formDays: days,
      formDaysSource: daysSource,
    });
    const effectiveRequiredStops = requestContract.requiredStops;
    if (effectiveRequiredStops.join("|") !== requiredStops.join("|")) {
      setRequiredStops(effectiveRequiredStops);
    }
    const parsedMultiPlan = parseMultiDestinationPlan(prompt);
    const multiPlan = parsedMultiPlan ?? multiDestinationPlan;
    if (parsedMultiPlan && parsedMultiPlan !== multiDestinationPlan) setMultiDestinationPlan(parsedMultiPlan);
    const effectiveDays =
      multiPlan
        ? multiPlan.stops.reduce((sum, stop) => sum + stop.days, 0)
        : (override?.days ??
          (daysSource === "manual" ? days : (requestContract.totalDays ?? requirements.requestedDays ?? days)));
    if (effectiveDays !== days) {
      setDays(effectiveDays);
      toast.info(`Ajustei para ${effectiveDays} dias, como pediste no texto.`);
    }
    const effectiveTransport = (override?.transport ??
      requiredGlobalTransport(requirements, transport)) as typeof transport;
    if (effectiveTransport !== transport) setTransport(effectiveTransport);

    const effectiveInterests = Array.from(
      new Set([...interests, ...requirements.preferredThemes, ...(override?.themes ?? [])]),
    );
    if (effectiveInterests.length !== interests.length) setInterests(effectiveInterests);

    // 2) Experiência icónica ambígua ⇒ perguntar antes de gastar créditos.
    const routePreflight = preflightProGeneration({
      prompt,
      days: effectiveDays,
      templateId: override?.templateId,
      templateMode: override?.templateMode ?? carriedMode ?? undefined,
      startRegion: cities || accommodation || prompt,
    });
    const namedTemplate = override
      ? (CURATED_ROUTE_TEMPLATES.find((t) => t.id === override.templateId) ??
        routePreflight.template ??
        null)
      : (routePreflight.template ?? matchTemplateByName(prompt));
    const scopeForTemplates = classifyGeoScope(cities || prompt, { language: "pt" });
    if (
      !override &&
      !namedTemplate &&
      !carriedTemplate &&
      requirements.requestedTransportExperiences.includes("scenic_train")
    ) {
      const options = resolveExperienceOptions();
      if (options.length) {
        setTemplateOptions(options);
        return;
      }
    }
    const template = namedTemplate ?? carriedTemplate ?? null;
    let effectiveTemplateMode = override?.templateMode ?? carriedMode;
    if (!override && template && !effectiveTemplateMode) {
      // Rota única (estrada, comboio, trilho) com dias suficientes: avança
      // automaticamente no percurso completo, sem perguntar o óbvio.
      if (shouldAutoSelectFullRoute(template, effectiveDays, prompt)) {
        effectiveTemplateMode = "full";
        setChosenTemplate(template);
        setTemplateMode("full");
        setTemplateOptions(null);
        if (template.transport === "car" && routeReturn === null) {
          toast.info(`Confirma se os ${effectiveDays} dias incluem o regresso antes de continuar.`);
          return;
        }
      } else {
        setChosenTemplate(template);
        setTemplateOptions(null);
        toast.info(`Escolhe como queres incluir ${template.name} antes de continuar.`);
        return;
      }
    }


    if (generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    setViolations([]);
    try {
      // 3) Um país/região não é uma cidade: expandir para bases reais antes de
      // qualquer chamada à IA ou ao Maps.
      // O contrato do pedido atual vence sempre estado antigo do formulário.
      const requiredStopKeys = new Set(
        effectiveRequiredStops.map((s) => s.trim().toLocaleLowerCase()),
      );
      const rawCities = (
        multiPlan
          ? multiPlan.stops.map((stop) => stop.destination).join(", ")
          : cities.trim()
            ? cities
            : requestContract.destinationBases.length
              ? requestContract.destinationBases.join(", ")
              : (scopeForTemplates.name ?? "")
      )
        .split(",")
        .map((c) => c.trim())
        // Palavras funcionais e locais obrigatórios nunca podem virar cidades.
        .filter(
          (c) =>
            Boolean(c) &&
            !isFunctionalRequestWord(c) &&
            !requiredStopKeys.has(c.toLocaleLowerCase()),
        );
      let expanded: string[] = override ? [...override.cities] : [];
      for (const entry of override ? [] : rawCities) {
        const scope = classifyGeoScope(entry, { language: "pt" });
        if (needsRegionalPlanner(scope)) {
          const bases = pickRegionalBases({
            countryCode: scope.countryCode,
            days: effectiveDays,
            themes: effectiveInterests.filter((i): i is RegionalBase["themes"][number] =>
              [
                "nature",
                "lakes",
                "cultural",
                "food",
                "mountains",
                "coast",
                "city",
                "scenic_rail",
              ].includes(i),
            ),
            preferTransit: effectiveTransport === "transit",
            preferAffordable: requirements.accommodationRequest?.priceSensitivity === "budget",
          });
          if (bases.length) {
            expanded.push(...bases.map((b) => b.city));
            continue;
          }
        }
        expanded.push(entry);
      }
      // A experiência curada determina primeiro a geografia. Só depois entram
      // cidades complementares, sem poder substituir as âncoras obrigatórias.
      if (template && effectiveTemplateMode && !override) {
        // A experiência curada determina o esqueleto completo do percurso.
        expanded = buildRouteSkeleton({
          template,
          days: routeReturn === "included" ? Math.max(1, effectiveDays - 1) : effectiveDays,
          mode: effectiveTemplateMode,
          startRegion: cities || accommodation || prompt,
        }).cities;
      }
      if (!override && expanded.length !== rawCities.length) {
        setCities(expanded.join(", "));
        toast.info(
          `Destino alargado: planeámos ${expanded.length} bases reais em vez de um país inteiro.`,
        );
      }

      if (override) {
        // A opção escolhida passa a ser o estado visível do formulário.
        setCities(expanded.join(", "));
        setTemplateMode(override.templateMode);
        if (template) setChosenTemplate(template);
      }

      // 4) Zonas de alojamento comparadas (sem inventar preços).
      const zoneCountryCode = scopeForTemplates.countryCode ?? template?.countryCodes[0];
      const needsZones = Boolean(requirements.accommodationRequest) || (override?.accommodationZoneMin ?? 0) > 0;
      const nextZones = needsZones
        ? compareAccommodationZones({
            countryCode: zoneCountryCode,
            cities: expanded,
            preferAffordable:
              requirements.accommodationRequest?.priceSensitivity === "budget" ||
              (override?.accommodationZoneMin ?? 0) >= 2,
            dates: { start: startDate || undefined, end: endDate || undefined },
            limit: Math.max(override?.accommodationZoneMin ?? 0, 2),
          })
        : [];
      setZones(nextZones);

      const promptDirectives = [
        template
          ? `Experiência obrigatória: ${template.name} (${template.anchors.join(" → ")}).`
          : "",
        override?.repairStrategy === "fewer_cities_full_experience"
          ? `Reparação vinculativa: mantém exatamente ${effectiveDays} dias, concentra o roteiro apenas em ${expanded.join(", ")} e prepara cada transferência no dia anterior sem reduzir as experiências pedidas.`
          : "",
        override?.repairStrategy === "more_nature_partial_experience"
          ? `Reparação vinculativa: mantém exatamente ${effectiveDays} dias e todas estas cidades (${expanded.join(", ")}); agenda cada transferência de forma realista, preparando-a no dia anterior, e remove primeiro visitas secundárias até todos os dias fecharem.`
          : "",
        wantsMotorcycle ? motorcycleDirective("pt") : "",
        specialtyPromptDirective(specialties, expanded),
      ].filter(Boolean);
      const promptWithTemplate = promptDirectives.length
        ? `${prompt.trim()}\n\n${promptDirectives.join("\n")}`
        : prompt.trim();

      const res = await generateFn({
        data: {
          prompt: promptWithTemplate,
          cities: expanded,
          accommodation: accommodation.trim() || undefined,
          originCoords: originCoords ?? undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          days: effectiveDays,
          transport: effectiveTransport,
          secondaryModes: localModes,
          rentalSuggestions: wantsRental,
          pace,
          interests: effectiveInterests,
          templateId: template?.id,
          templateMode: effectiveTemplateMode ?? undefined,
          routeReturn: override?.routeReturn ?? routeReturn ?? undefined,
          // Refeições: regra única — texto do pedido atual > default.
          mealPreference: resolveMealPreference({
            choice: "restaurant",
            eatMinutes: 90,
            requestText: prompt,
          }),
          repairStrategy: override?.repairStrategy as
            | "fewer_cities_full_experience"
            | "more_nature_partial_experience"
            | undefined,
          repairOfId: override ? (plan?.id ?? lastPlanIdRef.current ?? undefined) : undefined,
          accommodationZoneCount: nextZones.length,
          multiDestinationPlan: multiPlan ?? undefined,
          requiredStops: effectiveRequiredStops,
        },
      });

      setPlan(res);
      lastContextRef.current = contextInput;
      lastPlanIdRef.current = (res as { id?: string }).id ?? lastPlanIdRef.current;
      setIsSaved(false);
      setShareSlug(null);

      // 5) Verificar o plano contra o contrato — o resumo não conta, só paragens.
      const found = res.compliance?.violations?.length
        ? (res.compliance.violations as ComplianceViolation[])
        : checkPlanCompliance(
            requirements,
            {
              transport: res.transport,
              days: res.days.map((d) => ({
                day: d.day,
                city: d.city,
                stops: d.stops.map((s) => ({
                  name: s.name,
                  category: (s as { category?: string | null }).category ?? null,
                })),
              })),
            },
            {
              accommodationZoneCount: nextZones.length,
              recognizedTemplateId: template?.id,
              usedTemplateId: template?.id,
            },
          );
      setViolations(found);
      if (found.length) {
        toast.warning(
          "A reparação automática não conseguiu fechar todo o contrato. Não gastaste outro crédito e guardar/PDF estão bloqueados.",
        );
      } else {
        toast.success("Roteiro Pro gerado. Carrega em Guardar para o adicionar aos teus roteiros.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao gerar");
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  }

  async function onSave() {
    if (!plan?.id) return;
    if (!isPlanAcceptable(violations) || plan.compliance?.valid === false) {
      toast.error("Resolve primeiro os elementos em falta; este roteiro ainda não é entregável.");
      return;
    }
    setSaving(true);
    try {
      const next = !isSaved;
      await saveFn({ data: { id: plan.id, saved: next } });
      setIsSaved(next);
      toast.success(next ? "Roteiro guardado." : "Roteiro removido dos guardados.");
      const refreshed = await listFn();
      setHistory(refreshed as ListItem[]);
      if (next) {
        const row = await getFn({ data: { id: plan.id } });
        setShareSlug((row as { share_slug?: string | null }).share_slug ?? null);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  }

  async function onCopyShare() {
    if (!shareSlug) return;
    const url = buildProShareUrl(shareSlug);
    try {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      if (nav.share) {
        try {
          await nav.share({
            title: plan?.title ?? "Rota Pro",
            text: "Vê esta rota Routify Pro:",
            url,
          });
          return;
        } catch {
          /* fallback to copy */
        }
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!", { description: url });
    } catch {
      toast.error("Não foi possível copiar. Link: " + url);
    }
  }

  function onDownloadPdf() {
    if (!plan) return;
    if (!isPlanAcceptable(violations) || plan.compliance?.valid === false) {
      toast.error("O PDF só fica disponível quando o roteiro cumprir todo o pedido.");
      return;
    }
    exportProPlanPdf(plan).catch((err) =>
      toast.error(err instanceof Error ? err.message : "Erro ao gerar PDF"),
    );
  }

  async function onRegenDay(day: number) {
    if (!plan?.id) return;
    setRegeneratingDay(day);
    try {
      const next = await regenFn({ data: { id: plan.id, day } });
      setPlan({ ...plan, ...next, id: plan.id });
      toast.success(`Dia ${day} regenerado.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao regenerar");
    } finally {
      setRegeneratingDay(null);
    }
  }

  type StopT = PlanShape["days"][number]["stops"][number];
  function mutateDay(day: number, fn: (stops: StopT[]) => StopT[]) {
    if (!plan) return;
    const nextDays = plan.days.map((d) => (d.day === day ? { ...d, stops: fn(d.stops) } : d));
    setPlan({ ...plan, days: nextDays });
  }
  function removeStop(day: number, index: number) {
    mutateDay(day, (stops) => stops.filter((_, i) => i !== index));
  }
  function moveStop(day: number, index: number, dir: -1 | 1) {
    mutateDay(day, (stops) => {
      const arr = [...stops];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return arr;
    });
  }
  async function runSearch(day: number, meal: boolean, q: string) {
    if (!plan || !q.trim()) return;
    const dayObj = plan.days.find((d) => d.day === day);
    const anchor = dayObj?.stops.find((s) => s.lat != null && s.lng != null);
    setSearchLoading(true);
    try {
      const r = await searchFn({
        data: {
          query: q.trim(),
          near: anchor ? { lat: anchor.lat!, lng: anchor.lng! } : undefined,
          mealsOnly: meal,
        },
      });
      setSearchResults(r.results);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }
  function addOrReplace(
    place: { id: string; name: string; lat: number; lng: number },
    meal: boolean,
  ) {
    if (!plan) return;
    if (pendingReplace) {
      mutateDay(pendingReplace.day, (stops) => {
        const arr = [...stops];
        const old = arr[pendingReplace.index];
        arr[pendingReplace.index] = {
          ...old,
          name: place.name,
          lat: place.lat,
          lng: place.lng,
          placeId: place.id,
          note: undefined,
        };
        return arr;
      });
      setPendingReplace(null);
    } else if (addSearchFor) {
      mutateDay(addSearchFor.day, (stops) => {
        const newStop: StopT = {
          kind: meal ? "meal" : "poi",
          name: place.name,
          time: stops[stops.length - 1]?.time ?? "",
          durationMin: meal ? 60 : 45,
          lat: place.lat,
          lng: place.lng,
          placeId: place.id,
        };
        return [...stops, newStop];
      });
    }
    setAddSearchFor(null);
    setSearchQ("");
    setSearchResults([]);
  }
  async function onSaveEdits() {
    if (!plan?.id) return;
    setSavingEdits(true);
    try {
      const next = await updateFn({
        data: {
          id: plan.id,
          days: plan.days.map((d) => ({
            day: d.day,
            city: d.city,
            intro: d.intro,
            stops: d.stops,
          })),
        },
      });
      setPlan({ ...plan, ...next, id: plan.id });
      setViolations((next.compliance?.violations ?? []) as ComplianceViolation[]);
      setEditMode(false);
      toast.success("Alterações guardadas.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao guardar edições");
    } finally {
      setSavingEdits(false);
    }
  }

  function applyRepairCandidate(candidate: { id: string; name: string; lat: number; lng: number }) {
    if (!plan) return;
    const missingKind = violations.find(
      (violation) => violation.code === "missing_experience",
    )?.kind;
    if (missingKind !== "lake" && missingKind !== "activity") return;
    let bestDay = plan.days[0]?.day;
    let bestDistance = Infinity;
    for (const day of plan.days) {
      const anchor = day.stops.find((stop) => stop.lat != null && stop.lng != null);
      if (!anchor || anchor.lat == null || anchor.lng == null) continue;
      const distance = (anchor.lat - candidate.lat) ** 2 + (anchor.lng - candidate.lng) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        bestDay = day.day;
      }
    }
    if (bestDay == null) return;
    mutateDay(bestDay, (stops) => {
      const replacement = stops.findIndex(
        (stop) =>
          stop.kind === "poi" &&
          /museu|museum|igreja|church|praça|square|monument/iu.test(stop.name),
      );
      const next: StopT = {
        kind: "experience",
        category: missingKind,
        name: candidate.name,
        time: replacement >= 0 ? stops[replacement].time : "15:00",
        durationMin: missingKind === "lake" ? 90 : 120,
        lat: candidate.lat,
        lng: candidate.lng,
        placeId: candidate.id,
        note:
          missingKind === "lake"
            ? "Experiência lacustre escolhida pelo utilizador."
            : "Atividade real escolhida pelo utilizador.",
      };
      if (replacement < 0) return [...stops, next];
      return stops.map((stop, index) => (index === replacement ? next : stop));
    });
    setEditMode(true);
    toast.info("Candidato aplicado. Guarda a correção para voltar a validar sem gastar crédito.");
  }

  if (authLoading || hasPro === null) {
    return (
      <main className="mx-auto flex min-h-[60vh] w-full max-w-3xl items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Início
      </Link>

      <header className="mt-6 mb-8 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500/15 to-brand/15 px-3 py-1 text-xs font-semibold text-brand">
          <Sparkles className="h-3.5 w-3.5" /> Routify Pro
        </div>
        <h1 className="mt-3 text-4xl font-bold">Planeador Pro</h1>
        <p className="mt-2 text-muted-foreground">
          Multi-dia, multi-cidade, com otimização de rota e paragens para refeições.
        </p>
      </header>

      {!hasPro ? (
        <ProPaywall />
      ) : (
        <>
          <form
            onSubmit={onGenerate}
            className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4"
          >
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">
                Descreve a viagem
              </label>
              <textarea
                value={prompt}
                onChange={(e) => {
                  // Pedido novo: o contrato do texto volta a mandar.
                  setPrompt(e.target.value);
                  setDaysSource(null);
                  citiesTouchedRef.current = false;
                }}
                rows={3}
                placeholder="Ex: 5 dias em Barcelona e Girona com a família, foco em cultura e miradouros, ritmo calmo."
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                Deslocações locais (opcional)
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                O transporte principal liga as zonas; estes modos usam-se dentro de cada zona,
                só quando melhoram o percurso.
              </p>
              <div className="mt-2 flex flex-wrap gap-3">
                {(["walk", "bike", "scooter", "transit", "car", "motorcycle"] as TransportMode[])
                  .filter((mode) => mode !== (motorcycle ? "motorcycle" : transport))
                  .map((mode) => (
                    <label key={mode} className="inline-flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={localModes.includes(mode)}
                        onChange={(e) =>
                          setLocalModes((current) =>
                            e.target.checked
                              ? [...current, mode]
                              : current.filter((m) => m !== mode),
                          )
                        }
                      />
                      {transportModeLabel(mode, "pt")}
                    </label>
                  ))}
              </div>
              {["car", "motorcycle", "bike", "scooter"].includes(
                motorcycle ? "motorcycle" : transport,
              ) || localModes.some((m) => ["car", "motorcycle", "bike", "scooter"].includes(m)) ? (
                <label className="mt-3 inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={wantsRental}
                    onChange={(e) => setWantsRental(e.target.checked)}
                  />
                  Quero sugestões de aluguer (empresas reais perto do alojamento)
                </label>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">
                  Cidades (separadas por vírgula)
                </label>
                <input
                  value={cities}
                  onChange={(e) => {
                    citiesTouchedRef.current = true;
                    setCities(e.target.value);
                  }}
                  placeholder="Barcelona, Girona"
                  className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">
                  Dias
                </label>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={days}
                  onChange={(e) => {
                    if (multiDestinationPlan) return;
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next) || next < 1) return;
                    // Edição manual: passa a ser a fonte do contrato atual.
                    setDays(next);
                    setDaysSource("manual");
                  }}
                  readOnly={Boolean(multiDestinationPlan)}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">
                  Transporte
                </label>
                <select
                  value={motorcycle ? "motorcycle" : transport}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "motorcycle") {
                      setMotorcycle(true);
                      setTransport("car");
                      return;
                    }
                    setMotorcycle(false);
                    setTransport(value as typeof transport);
                  }}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="walk">A pé</option>
                  <option value="bike">Bicicleta</option>
                  <option value="motorcycle">{transportModeLabel("motorcycle", "pt")}</option>
                  <option value="car">Carro</option>
                  <option value="transit">Transportes públicos</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">
                  Ritmo
                </label>
                <select
                  value={pace}
                  onChange={(e) => setPace(e.target.value as typeof pace)}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="slow">Calmo</option>
                  <option value="normal">Normal</option>
                  <option value="intense">Intenso</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">
                  Data de início (opcional)
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">
                  Data de fim (opcional)
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <p className="sm:col-span-2 text-[11px] text-muted-foreground">
                Se mencionares um "dia de praia", "zoo", "parque aquático" ou similar na descrição,
                o Pro dedica um dia inteiro a isso — e escolhe o melhor dia consoante a meteo
                prevista.
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                <Home className="h-3 w-3" /> Alojamento / ponto de partida (opcional)
              </label>
              <input
                value={accommodation}
                onChange={(e) => {
                  setAccommodation(e.target.value);
                  if (e.target.value) setOriginCoords(null);
                }}
                placeholder="Ex: Hotel Nhow Milano, Via Tortona 35, Milão"
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  disabled={locating}
                  onClick={() => {
                    if (!navigator.geolocation) {
                      toast.error("Geolocalização não disponível neste dispositivo.");
                      return;
                    }
                    setLocating(true);
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setOriginCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                        setAccommodation("");
                        setLocating(false);
                        toast.success(
                          "Localização capturada — o roteiro vai partir daí (se estiveres na cidade).",
                        );
                      },
                      () => {
                        setLocating(false);
                        toast.error("Não consegui obter a tua localização.");
                      },
                      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
                    );
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1 text-xs hover:bg-accent disabled:opacity-60"
                >
                  {locating ? <Loader2 className="h-3 w-3 animate-spin" /> : "📍"} Usar minha
                  localização
                </button>
                {originCoords && (
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400">
                    Localização definida ✓{" "}
                    <button
                      type="button"
                      onClick={() => setOriginCoords(null)}
                      className="underline ml-1"
                    >
                      limpar
                    </button>
                  </span>
                )}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Se não indicares nada, o Routify parte da estação central da cidade (ex: Milano
                Centrale) para evitar rotas ao calhas.
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-muted-foreground">
                Interesses
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {INTERESTS.map((i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => toggleInterest(i)}
                    className={`rounded-full border px-3 py-1 text-xs ${interests.includes(i) ? "border-brand bg-brand text-white" : "border-input bg-background hover:bg-accent"}`}
                  >
                    {INTEREST_LABEL[i] ?? i}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={generating}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand/90 disabled:opacity-60"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Criar rota Pro
            </button>
          </form>

          {templateOptions && (
            <section className="mt-6 rounded-2xl border border-brand/40 bg-brand/5 p-4">
              <h2 className="text-sm font-semibold">
                Qual é a experiência que queres mesmo fazer?
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Falaste num comboio panorâmico. Escolhe uma opção real — ainda não gastámos
                créditos.
              </p>
              {templateOptions.length === 0 && (
                <p className="mt-3 rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
                  Não tenho experiências curadas verificadas para este destino. Escreve o nome
                  oficial em baixo e eu reconstruo o roteiro.
                </p>
              )}
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {templateOptions.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => chooseExperience(t)}
                    className="rounded-xl border border-border bg-card p-3 text-left transition hover:border-brand"
                  >
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t.anchors.join(" → ")}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {t.approxKm
                        ? `≈ ${t.approxKm} km · ${recommendedRouteDays(t)} dias`
                        : `≈ ${t.approxHours}h`}
                      {t.note ? ` · ${t.note}` : ""}
                    </p>

                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setTemplateOptions(null)}
                className="mt-3 text-xs font-medium text-muted-foreground underline"
              >
                Nenhuma destas — continuar sem comboio panorâmico
              </button>
              <div className="mt-3 flex gap-2">
                <input
                  value={otherTemplate}
                  onChange={(event) => setOtherTemplate(event.target.value)}
                  placeholder="Outro, por exemplo GoldenPass Express"
                  className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-xs"
                />
                <button
                  type="button"
                  onClick={() => {
                    const match = matchTemplateByName(otherTemplate);
                    const activeCountry = classifyGeoScope(cities || prompt, { language: "pt" }).countryCode;
                    if (!match || (activeCountry && !match.countryCodes.includes(activeCountry))) {
                      toast.error("Não encontrei essa experiência no catálogo deste destino. Escreve o nome oficial.");
                      return;
                    }
                    setOtherTemplate("");
                    chooseExperience(match);
                  }}
                  className="rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-brand-foreground"
                >
                  Continuar
                </button>
              </div>
            </section>
          )}

          {chosenTemplate && !templateOptions && (() => {
            const modeCopy = experienceModeCopy(chosenTemplate, { language: "pt", days });
            return (
            <section className="mt-6 rounded-2xl border border-brand/40 bg-brand/5 p-4">
              <h2 className="text-sm font-semibold">{modeCopy.title}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{modeCopy.hint}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {modeCopy.options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    disabled={generating}
                    onClick={() => applyExperienceMode(chosenTemplate, option.id)}
                    className={`rounded-xl border p-3 text-left disabled:opacity-60 ${templateMode === option.id ? "border-brand bg-brand/10" : "border-border bg-card"}`}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      {generating && templateMode === option.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : null}
                      {option.label}
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  </button>
                ))}
              </div>

              {templateMode === "full" && chosenTemplate.transport === "car" && routeReturn === null ? (
                <div className="mt-4 border-t border-border pt-4">
                  <h3 className="text-sm font-semibold">Os {days} dias incluem o regresso?</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Isto define quantos dias ficam disponíveis para percorrer a estrada de ponta a ponta.
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      disabled={generating}
                      onClick={() => void applyExperienceMode(chosenTemplate, "full", "excluded")}
                      className="rounded-xl border border-border bg-card p-3 text-left hover:border-brand disabled:opacity-60"
                    >
                      <span className="block text-sm font-semibold">Não, são {days} dias só para a rota</span>
                      <span className="mt-1 block text-xs text-muted-foreground">O regresso fica fora deste roteiro.</span>
                    </button>
                    <button
                      type="button"
                      disabled={generating || days < 2}
                      onClick={() => void applyExperienceMode(chosenTemplate, "full", "included")}
                      className="rounded-xl border border-border bg-card p-3 text-left hover:border-brand disabled:opacity-60"
                    >
                      <span className="block text-sm font-semibold">Sim, o regresso conta nos {days} dias</span>
                      <span className="mt-1 block text-xs text-muted-foreground">{Math.max(1, days - 1)} dias de rota + 1 dia de regresso.</span>
                    </button>
                  </div>
                </div>
              ) : null}

              <button type="button" onClick={() => { setChosenTemplate(null); setTemplateMode(null); }} className="mt-3 text-xs underline text-muted-foreground">
                Remover experiência
              </button>
            </section>
            );
          })()}


          {contractQuestionsList.length > 0 && (
            <section className="mt-6 rounded-2xl border border-brand/40 bg-brand/5 p-4">
              {contractQuestionsList.map((question) => (
                <div key={question.code} className="mb-2 last:mb-0">
                  <h2 className="text-sm font-semibold">{question.question}</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Responder é gratuito e não consome créditos.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {question.options.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => answerContractQuestion(option.label)}
                        className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-brand hover:bg-accent"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {(violations.length > 0 ||
            Boolean(plan?.compliance?.repairStrategies?.options?.length) ||
            Boolean(plan?.compliance?.repairAsk)) && (
            <section className="mt-6 rounded-2xl border border-destructive/40 bg-destructive/5 p-4">
              {violations.length > 0 ? (
                <>
                  <h2 className="text-sm font-semibold text-destructive">
                    O roteiro não cumpre o teu pedido
                  </h2>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                    {violations.map((v) => (
                      <li key={v.code + v.message}>{v.message}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    A quota foi devolvida. Escolhe abaixo um candidato real ou ajusta manualmente o
                    roteiro; não será cobrado novo crédito.
                  </p>
                </>
              ) : null}
              {plan?.compliance?.repairCandidates?.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {plan.compliance.repairCandidates.map((candidate) => (
                    <button
                      key={candidate.id}
                      type="button"
                      onClick={() => applyRepairCandidate(candidate)}
                      className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-brand hover:bg-accent"
                    >
                      Usar {candidate.name}
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Opções que reconstroem cidades → transferências → horários
                  antes das atrações. Não consomem novo crédito. */}
              {plan?.compliance?.repairStrategies?.options?.length ? (
                <div className="mt-4 rounded-xl border border-border bg-background/70 p-3">
                  <p className="text-xs font-semibold">
                    {plan.compliance.repairStrategies.message}
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {plan.compliance.repairStrategies.options.map((option) => (
                      <Button
                        key={option.id}
                        type="button"
                        variant="outline"
                        disabled={generating}
                        onClick={() =>
                          void onGenerate(null, {
                            repairStrategy: option.id,
                            cities: option.patch.cities,
                            days: option.patch.days,
                            transport: option.patch.transport,
                            templateId: option.patch.templateId,
                            templateMode: option.patch.templateMode,
                            accommodationZoneMin: option.patch.accommodationZoneMin,
                            themes: option.patch.themes,
                          })
                        }
                        className="h-auto min-w-0 whitespace-normal rounded-xl bg-card p-3 text-left hover:border-brand"
                      >
                        <span className="block min-w-0">
                          <span className="block text-sm font-semibold">{option.label}</span>
                          <span className="mt-1 block text-xs font-normal text-muted-foreground">
                            {option.description}
                          </span>
                        </span>
                      </Button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    {plan.compliance.repairStrategies.hint}
                  </p>
                </div>
              ) : null}

              {plan?.compliance?.repairAsk ? (
                <div className="mt-4 rounded-xl border border-border bg-background/70 p-3 text-xs">
                  <p className="font-semibold">{plan.compliance.repairAsk.message}</p>
                  <p className="mt-1 text-muted-foreground">{plan.compliance.repairAsk.hint}</p>
                  <button
                    type="button"
                    onClick={() => {
                      // Abre sempre o seletor: com catálogo compatível ou, em
                      // último caso, com o campo livre lá dentro.
                      setTemplateOptions(resolveExperienceOptions());
                    }}
                    className="mt-3 rounded-lg bg-brand px-3 py-2 font-semibold text-brand-foreground"
                  >
                    Escolher experiência
                  </button>
                </div>
              ) : null}
            </section>
          )}


          {zones.length > 0 && (
            <section className="mt-6 rounded-2xl border border-border bg-card p-4">
              <h2 className="text-sm font-semibold">Zonas onde ficar</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {zones.map((z) => (
                  <div key={z.id} className="rounded-xl border border-border p-3">
                    <p className="text-sm font-semibold">
                      {z.city} · {z.region}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{z.priceLabel}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {z.whyHere}. {z.transitNote}
                    </p>
                    <a
                      href={z.searchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-xs font-medium text-brand underline"
                    >
                      Ver disponibilidade
                    </a>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">{ACCOMMODATION_DISCLAIMER}</p>
            </section>
          )}

          {plan && (
            <section className="mt-8 space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold">{plan.title}</h2>
                  <p className="text-sm text-muted-foreground">
                    {plan.days.length} dia{plan.days.length !== 1 && "s"} • Transporte:{" "}
                    {TRANSPORT_LABEL[plan.transport] ?? plan.transport} • Ritmo:{" "}
                    {PACE_LABEL[plan.pace] ?? plan.pace}
                    {plan.mood ? ` • Tom: ${MOOD_LABEL[plan.mood] ?? plan.mood}` : ""}
                  </p>
                </div>
                {plan.id && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={onDownloadPdf}
                      disabled={!isPlanAcceptable(violations) || plan.compliance?.valid === false}
                      title={!isPlanAcceptable(violations) || plan.compliance?.valid === false ? "Disponível quando o roteiro cumprir todo o pedido." : undefined}
                      className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2 text-sm font-semibold hover:bg-accent disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      <Download className="h-4 w-4" /> PDF
                    </button>
                    {isSaved && shareSlug && (
                      <button
                        onClick={onCopyShare}
                        disabled={!isPlanAcceptable(violations) || plan.compliance?.valid === false}
                        title={
                          !isPlanAcceptable(violations) || plan.compliance?.valid === false
                            ? "Disponível quando o roteiro cumprir todo o pedido."
                            : "Copia a ligação pública deste roteiro (www.routify-tour.com) para enviar a alguém."
                        }
                        className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2 text-sm font-semibold hover:bg-accent disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        <Share2 className="h-4 w-4" /> Partilhar rota
                      </button>
                    )}
                    {!isPlanAcceptable(violations) || plan.compliance?.valid === false ? (
                      <p className="basis-full text-right text-[11px] text-destructive">
                        Guardar, PDF e Partilhar ficam disponíveis depois de resolver os elementos em falta.
                      </p>
                    ) : null}
                    {editMode ? (
                      <button
                        onClick={onSaveEdits}
                        disabled={savingEdits}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        {savingEdits ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <BookmarkCheck className="h-4 w-4" />
                        )}
                        Guardar edições
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditMode(true)}
                        className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
                      >
                        <Pencil className="h-4 w-4" /> Editar
                      </button>
                    )}
                    <button
                      onClick={onSave}
                      disabled={
                        saving || !isPlanAcceptable(violations) || plan.compliance?.valid === false
                      }
                      title={!isPlanAcceptable(violations) || plan.compliance?.valid === false ? "Resolve primeiro os elementos em falta." : undefined}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${isSaved ? "bg-brand text-white" : "border border-input bg-background hover:bg-accent"}`}
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isSaved ? (
                        <BookmarkCheck className="h-4 w-4" />
                      ) : (
                        <Bookmark className="h-4 w-4" />
                      )}
                      {isSaved ? "Guardado" : "Guardar roteiro"}
                    </button>
                  </div>
                )}
              </div>

              {plan.suggestions &&
                (plan.suggestions.hotel ||
                  (plan.suggestions.restaurants?.length ?? 0) > 0 ||
                  (plan.suggestions.rentals?.length ?? 0) > 0 ||
                  plan.suggestions.brief) && (
                  <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-card to-brand/5 p-5 shadow-sm">
                    <div className="mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                        {
                          {
                            pt: "Sugestões personalizadas",
                            en: "Personalised suggestions", it: "Suggerimenti personalizzati",
                            es: "Sugerencias personalizadas",
                            fr: "Suggestions personnalisées",
                            de: "Personalisierte Vorschläge",
                            ja: "パーソナライズされた提案",
                          }[lang]
                        }
                      </h3>
                    </div>
                    {plan.suggestions.brief && (
                      <p className="mb-3 text-sm italic text-muted-foreground">
                        "{plan.suggestions.brief}"
                      </p>
                    )}
                    {plan.suggestions.hotel && (
                      <div className="mb-4">
                        <div className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                          <Home className="h-3 w-3" /> Hotel recomendado
                        </div>
                        <a
                          href={mapsUrlForStop({
                            name: plan.suggestions.hotel.name,
                            lat: plan.suggestions.hotel.lat,
                            lng: plan.suggestions.hotel.lng,
                            placeId: plan.suggestions.hotel.id,
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 block text-sm font-semibold text-brand hover:underline"
                        >
                          {plan.suggestions.hotel.name}
                          {plan.suggestions.hotel.rating != null && (
                            <span className="ml-2 text-xs text-amber-500">
                              ★ {plan.suggestions.hotel.rating.toFixed(1)}
                            </span>
                          )}
                        </a>
                        {plan.suggestions.hotel.address && (
                          <div className="text-xs text-muted-foreground">
                            {plan.suggestions.hotel.address}
                          </div>
                        )}
                        {plan.suggestions.hotel.note && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {plan.suggestions.hotel.note}
                          </div>
                        )}
                      </div>
                    )}
                    {plan.suggestions.rentals && plan.suggestions.rentals.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold uppercase text-muted-foreground">
                          Aluguer sugerido
                        </div>
                        <ul className="mt-1 grid gap-2 sm:grid-cols-2">
                          {plan.suggestions.rentals.map((r) => (
                            <li key={r.id} className="rounded-lg border border-border bg-background/60 p-2">
                              <a
                                href={mapsUrlForStop({ name: r.name, lat: r.lat, lng: r.lng, placeId: r.id })}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-brand hover:underline"
                              >
                                {r.name}
                              </a>
                              {r.address && (
                                <div className="text-[11px] text-muted-foreground">{r.address}</div>
                              )}
                              {r.pickupZone && (
                                <div className="text-[11px] text-muted-foreground">
                                  Zona sugerida de levantamento e devolução: {r.pickupZone}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {plan.suggestions.restaurants && plan.suggestions.restaurants.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                          <Utensils className="h-3 w-3" /> Restaurantes imperdíveis
                        </div>
                        <ul className="mt-1 grid gap-2 sm:grid-cols-2">
                          {plan.suggestions.restaurants.map((r) => (
                            <li
                              key={r.id}
                              className="rounded-lg border border-border bg-background/60 p-2"
                            >
                              <a
                                href={mapsUrlForStop({
                                  name: r.name,
                                  lat: r.lat,
                                  lng: r.lng,
                                  placeId: r.id,
                                })}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-brand hover:underline"
                              >
                                {r.name}
                                {r.rating != null && (
                                  <span className="ml-2 text-xs text-amber-500">
                                    ★ {r.rating.toFixed(1)}
                                  </span>
                                )}
                              </a>
                              {r.address && (
                                <div className="text-[11px] text-muted-foreground">{r.address}</div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

              {Array.from(new Set(plan.days.map((d) => d.city)))
                .slice(0, 3)
                .map((city) => (
                  <MobilityTicketCard key={city} city={city} transport={plan.transport} />
                ))}

              {plan.days.map((d, dayIndex) => {
                const scenicRoad =
                  d.stops.some((stop) => stop.category === "scenic_road") ||
                  (chosenTemplate?.transport === "car" && d.stops.every((stop) => stop.category !== "return"));
                const navigableStops = scenicRoad
                  ? d.stops.filter(
                      (stop) =>
                        stop.category === "scenic_road" &&
                        (!chosenTemplate || stop.templateId === chosenTemplate.id),
                    )
                  : d.stops;
                const corridor = corridorForTemplate(chosenTemplate?.id);
                const strictStops = corridor
                  ? strictCorridorNavigationStops(corridor, navigableStops as any)
                  : navigableStops;
                const hasCoords = (stop: { lat?: number | null; lng?: number | null }) =>
                  typeof stop.lat === "number" && typeof stop.lng === "number";
                const lodgingOf = (day: (typeof plan.days)[number] | undefined) =>
                  day?.stops.filter((stop) => stop.category === "lodging").find(hasCoords) ?? null;
                // Numa etapa de estrada o dia começa onde se dormiu na véspera
                // e termina no hotel da noite: assim o check-in fica no fim do
                // percurso e ninguém tem de procurar onde dormir.
                const startLodging = scenicRoad ? lodgingOf(plan.days[dayIndex - 1]) : null;
                const endLodging = scenicRoad ? lodgingOf(d) : null;
                const asPoint = (stop: { lat?: number | null; lng?: number | null; name: string }) => ({
                  lat: stop.lat as number,
                  lng: stop.lng as number,
                  name: stop.name,
                });
                const routeStops = [
                  ...(startLodging ? [asPoint(startLodging)] : []),
                  ...(strictStops as typeof navigableStops).filter(hasCoords).map(asPoint),
                  ...(endLodging ? [asPoint(endLodging)] : []),
                ];
                // Numa etapa de estrada panorâmica o Google não pode desviar
                // para a autoestrada: o dia é a própria estrada.
                const dayUrl = buildRoutePreviewUrl({
                  stops: routeStops,
                  mode: plan.transport,
                  avoidHighways: scenicRoad || corridorRequiresAvoidHighways(corridor),
                });

                return (
                  <div
                    key={d.day}
                    className="rounded-2xl border border-border bg-card p-5 shadow-sm"
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h3 className="text-lg font-semibold">
                          Dia {d.day} — {d.city}
                        </h3>
                        {d.intro && <p className="text-sm text-muted-foreground">{d.intro}</p>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          const segments = buildModeSegmentUrls(
                            groupModeSegments(d.stops as never),
                          );
                          const multimodal =
                            new Set(segments.map((segment) => segment.mode)).size > 1;
                          // Um percurso multimodal não cabe num link único:
                          // damos um link por troço, com o modo correto.
                          if (multimodal) {
                            return segments.map((segment, index) => (
                              <GoogleRouteButton
                                key={`${segment.mode}-${index}`}
                                url={segment.url}
                                label={`Troço ${index + 1} ${segment.label}`}
                              />
                            ));
                          }
                          return dayUrl ? (
                            <GoogleRouteButton url={dayUrl} label="Abrir rota no Google Maps" />
                          ) : null;
                        })()}
                        <button
                          onClick={() => onRegenDay(d.day)}
                          disabled={regeneratingDay === d.day}
                          className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1 text-xs hover:bg-accent disabled:opacity-60"
                        >
                          {regeneratingDay === d.day ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                          )}
                          Regenerar dia
                        </button>
                      </div>
                    </div>
                    {d.stops.length > 0 && (() => {
                      const first = d.stops.find((s) => typeof s.lat === "number" && typeof s.lng === "number");
                      return (
                        <NavigationReadinessBar
                          className="mb-3"
                          startPoint={first ? { lat: first.lat as number, lng: first.lng as number } : null}
                          startPointName={d.stops[0]?.name}
                          routeStops={routeStops}
                          mode={plan.transport}
                        />
                      );
                    })()}
                    {d.stops.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Sem paragens para este dia.</p>
                    ) : (
                      <ol className="space-y-3">
                        {d.stops.map((s, i) => (
                          <li key={i} className="flex gap-3 border-l-2 border-brand/40 pl-3">
                            <div className="flex-1">
                              {s.travelFromPrev && (
                                <div className="mb-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                  <ArrowRight className="h-3 w-3" />
                                  {s.travelFromPrev.minutes} min {s.travelFromPrev.label} (
                                  {(s.travelFromPrev.meters / 1000).toFixed(1)} km)
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-sm font-medium">
                                {s.kind === "meal" ? (
                                  <Utensils className="h-4 w-4 text-amber-500" />
                                ) : (
                                  <MapPin className="h-4 w-4 text-brand" />
                                )}
                                {s.lat || s.placeId ? (
                                  <a
                                    href={mapsUrlForStop(s)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                  >
                                    {s.name}
                                  </a>
                                ) : (
                                  <span>{s.name}</span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="h-3 w-3" /> {s.time} • {s.durationMin} min
                                </span>
                                {(s.lat || s.placeId) && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openStopDetails(s)}
                                      className="inline-flex items-center gap-1 text-brand hover:underline"
                                    >
                                      <MapPin className="h-3 w-3" /> Ver fotos e reviews
                                    </button>
                                    <a
                                      href={navUrlForStop(s)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-brand hover:underline"
                                    >
                                      <Navigation className="h-3 w-3" /> Navegar
                                    </a>
                                  </>
                                )}

                              </div>
                              {s.note && (
                                <p className="mt-1 text-sm text-muted-foreground">{s.note}</p>
                              )}
                              {s.officialUrl && (
                                <a
                                  href={s.officialUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-1 inline-block text-xs font-medium text-brand underline"
                                >
                                  Confirmar horários e reservar no site oficial
                                </a>
                              )}
                              {editMode && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  <button
                                    onClick={() => moveStop(d.day, i, -1)}
                                    disabled={i === 0}
                                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-[11px] hover:bg-accent disabled:opacity-40"
                                  >
                                    <ChevronUp className="h-3 w-3" /> Subir
                                  </button>
                                  <button
                                    onClick={() => moveStop(d.day, i, 1)}
                                    disabled={i === d.stops.length - 1}
                                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-[11px] hover:bg-accent disabled:opacity-40"
                                  >
                                    <ChevronDown className="h-3 w-3" /> Descer
                                  </button>
                                  {s.kind === "meal" && (
                                    <button
                                      onClick={() => {
                                        setPendingReplace({ day: d.day, index: i });
                                        setAddSearchFor({ day: d.day, meal: true });
                                        setSearchQ("");
                                        setSearchResults([]);
                                      }}
                                      className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-[11px] hover:bg-accent"
                                    >
                                      <RefreshCw className="h-3 w-3" /> Trocar restaurante
                                    </button>
                                  )}
                                  <button
                                    onClick={() => removeStop(d.day, i)}
                                    className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive hover:bg-destructive/20"
                                  >
                                    <Trash2 className="h-3 w-3" /> Remover
                                  </button>
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    )}
                    {editMode && (
                      <div className="mt-4 border-t border-border pt-3">
                        {addSearchFor?.day === d.day ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                                <input
                                  autoFocus
                                  value={searchQ}
                                  onChange={(e) => setSearchQ(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      runSearch(d.day, addSearchFor.meal, searchQ);
                                    }
                                  }}
                                  placeholder={
                                    addSearchFor.meal
                                      ? "Ex: trattoria, sushi, vegan…"
                                      : "Nome do local ou tipo (ex: museu, parque)"
                                  }
                                  className="w-full rounded-lg border border-input bg-background pl-7 pr-3 py-2 text-sm"
                                />
                              </div>
                              <button
                                onClick={() => runSearch(d.day, addSearchFor.meal, searchQ)}
                                disabled={searchLoading || !searchQ.trim()}
                                className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand/90 disabled:opacity-60"
                              >
                                {searchLoading ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  "Procurar"
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setAddSearchFor(null);
                                  setPendingReplace(null);
                                  setSearchQ("");
                                  setSearchResults([]);
                                }}
                                className="inline-flex items-center justify-center rounded-lg border border-input bg-background p-2 hover:bg-accent"
                                aria-label="Cancelar"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {searchResults.length > 0 && (
                              <ul className="max-h-64 divide-y divide-border overflow-auto rounded-lg border border-border bg-background">
                                {searchResults.map((r) => (
                                  <li key={r.id}>
                                    <button
                                      onClick={() => addOrReplace(r, addSearchFor.meal)}
                                      className="flex w-full items-start justify-between gap-2 p-2 text-left text-sm hover:bg-accent"
                                    >
                                      <div>
                                        <div className="font-medium">{r.name}</div>
                                        <div className="text-[11px] text-muted-foreground">
                                          {r.address}
                                        </div>
                                      </div>
                                      {r.rating != null && (
                                        <span className="text-[11px] text-amber-500">
                                          ★ {r.rating.toFixed(1)}
                                        </span>
                                      )}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => {
                                setAddSearchFor({ day: d.day, meal: false });
                                setPendingReplace(null);
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1 text-xs hover:bg-accent"
                            >
                              <Plus className="h-3 w-3" /> Adicionar paragem
                            </button>
                            <button
                              onClick={() => {
                                setAddSearchFor({ day: d.day, meal: true });
                                setPendingReplace(null);
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1 text-xs hover:bg-accent"
                            >
                              <Utensils className="h-3 w-3" /> Adicionar refeição
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {history.length > 0 && (
            <section className="mt-10">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                Os teus roteiros Pro guardados
              </h3>
              <ul className="mt-3 divide-y divide-border rounded-2xl border border-border bg-card">
                {history.map((h) => (
                  <li key={h.id}>
                    <Link
                      to="/pro"
                      search={{ id: h.id }}
                      className="flex items-center justify-between p-3 text-sm hover:bg-accent/50 transition"
                    >
                      <span className="font-medium">{h.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(h.created_at).toLocaleDateString("pt-PT")}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <Dialog open={Boolean(detailStop)} onOpenChange={(open) => !open && setDetailStop(null)}>
            <DialogContent className="max-h-[90vh] overflow-y-auto p-0 sm:max-w-lg">
              {detailStop && (
                <>
                  <div className="aspect-[16/9] w-full bg-muted">
                    {detailPhoto ? (
                      <img src={detailPhoto} alt={detailStop.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                        {detailLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sem fotografia disponível"}
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 p-5">
                    <DialogHeader><DialogTitle>{detailStop.name}</DialogTitle></DialogHeader>
                    {detailLoading ? <p className="text-sm text-muted-foreground">A carregar detalhes…</p> : (
                      <div className="flex flex-wrap gap-2">
                        {detailInfo?.website && <a href={detailInfo.website} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-brand underline">Site oficial</a>}
                        {detailInfo?.phone && <a href={`tel:${detailInfo.phone}`} className="text-sm font-medium text-brand underline">{detailInfo.phone}</a>}
                        {detailInfo?.googleMapsUri && <a href={detailInfo.googleMapsUri} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-brand underline">Reviews no Google Maps</a>}
                      </div>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </main>
  );
}
